/* iter-palette-commit — Global theme applier.
 *
 * On first import, fetches `/api/theme` and mirrors any admin-set
 * HSL tuples onto `document.documentElement` as CSS variables
 * (`--background`, `--primary`, `--accent`, `--border`). Missing
 * fields fall back to the ship-committed values in `index.css`.
 *
 * Also exports `applyThemePreview` so the Palette dialog can flip
 * variables live before committing.
 */
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const TOKENS = {
  background: '--background',
  primary:    '--primary',
  accent:     '--accent',
  border:     '--border',
};

let _snapshot = null;    // Ship-committed values from `index.css`.

/** Freeze the ship defaults so Reset can restore them precisely. */
function snapshotShipDefaults() {
  if (_snapshot || typeof document === 'undefined') return;
  const s = getComputedStyle(document.documentElement);
  _snapshot = {};
  for (const key of Object.keys(TOKENS)) {
    _snapshot[key] = s.getPropertyValue(TOKENS[key]).trim();
  }
}

export function applyThemePatch(patch) {
  if (typeof document === 'undefined') return;
  snapshotShipDefaults();
  for (const [k, v] of Object.entries(patch || {})) {
    const varName = TOKENS[k];
    if (!varName) continue;
    if (v) document.documentElement.style.setProperty(varName, v);
    else   document.documentElement.style.removeProperty(varName);
  }
  // iter-focus-ring-boost — Recompute keyboard focus ring visibility
  // whenever the theme changes.
  ensureFocusRingVisible();
}

// ---------- Focus-ring guard ----------------------------------------
// When the primary × background pair drops below 3:1 contrast, the
// default `--ring: var(--primary)` becomes invisible on the canvas.
// We bump `--ring` to the dark ink token temporarily so keyboard
// focus stays legible; clear it as soon as the pair recovers.
function _hslTupleToRgb(tuple) {
  const m = /^(\d{1,3})\s+(\d{1,3})%\s+(\d{1,3})%$/.exec((tuple || '').trim());
  if (!m) return null;
  const h = (+m[1] % 360) / 360, s = +m[2] / 100, l = +m[3] / 100;
  if (s === 0) return { r: l, g: l, b: l };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const c = (t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return { r: c(h + 1 / 3), g: c(h), b: c(h - 1 / 3) };
}
function _lum({ r, g, b }) {
  const f = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
function _contrastFromHsl(hslA, hslB) {
  const A = _hslTupleToRgb(hslA), B = _hslTupleToRgb(hslB);
  if (!A || !B) return null;
  const la = _lum(A), lb = _lum(B);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export function ensureFocusRingVisible() {
  if (typeof document === 'undefined') return;
  const s = getComputedStyle(document.documentElement);
  const bg = s.getPropertyValue('--background').trim();
  const pr = s.getPropertyValue('--primary').trim();
  const ratio = _contrastFromHsl(bg, pr);
  if (ratio != null && ratio < 3.0) {
    // Ink token (`--ink`) — dark, always readable.
    document.documentElement.style.setProperty('--ring', '24 18% 14%');
    document.documentElement.dataset.ringBoosted = '1';
  } else if (document.documentElement.dataset.ringBoosted === '1') {
    document.documentElement.style.removeProperty('--ring');
    delete document.documentElement.dataset.ringBoosted;
  }
}

/** Session-only preview — same as commit but marked so refresh reverts. */
export const applyThemePreview = applyThemePatch;

/** Restore the ship-committed values (as read from index.css on boot). */
export function resetThemeToShipDefaults() {
  if (!_snapshot || typeof document === 'undefined') return;
  for (const key of Object.keys(TOKENS)) {
    document.documentElement.style.removeProperty(TOKENS[key]);
  }
}

let _bootstrapped = false;
/** Fetch and apply the DB-committed theme once per SPA session. */
export async function bootstrapTheme() {
  if (_bootstrapped) return;
  _bootstrapped = true;
  snapshotShipDefaults();
  try {
    const r = await axios.get(`${API}/theme`, { timeout: 6000 });
    applyThemePatch(r?.data || {});
  } catch { /* silent — falls back to index.css defaults */ }
}
